import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Table, Tabs, Tab, Modal, Alert } from 'react-bootstrap';
import './SuperAdminDashboard.css';

function SuperAdminDashboard({ userInfo }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [mobileLogoFile, setMobileLogoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [mobilePreviewUrl, setMobilePreviewUrl] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [mobileUploadStatus, setMobileUploadStatus] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState('companies');
  const [message, setMessage] = useState('');
  
  // グループ属性の状態
  const [clientCompanies, setClientCompanies] = useState([]);
  const [jobAssignments, setJobAssignments] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [selectedClientCompany, setSelectedClientCompany] = useState('');
  
  // モーダル関連の状態
  const [showClientCompanyModal, setShowClientCompanyModal] = useState(false);
  const [showJobAssignmentModal, setShowJobAssignmentModal] = useState(false);
  const [showEmploymentTypeModal, setShowEmploymentTypeModal] = useState(false);
  const [currentItem, setCurrentItem] = useState({ name: '', description: '' });
  const [isEditing, setIsEditing] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // スーパー管理者権限チェック
    if (!userInfo || userInfo.role !== 'super_admin') {
      navigate('/time-clock');
      return;
    }

    fetchCompanies();
  }, [userInfo, navigate]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/companies', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('企業一覧の取得に失敗しました');
      }

      const data = await response.json();
      setCompanies(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const value = e.target.value;
    console.log('Company selected:', value);
    setSelectedCompany(value);
    setPreviewUrl('');
    setMobilePreviewUrl('');
    setLogoFile(null);
    setMobileLogoFile(null);
    setUploadStatus('');
    setMobileUploadStatus('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    console.log('File selected:', file);
    if (file) {
      setLogoFile(file);
      console.log('Logo file set:', file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        console.log('Preview URL set');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMobileFileChange = (e) => {
    const file = e.target.files[0];
    console.log('Mobile file selected:', file);
    if (file) {
      // ファイルサイズチェック（2MB以下）
      if (file.size > 2 * 1024 * 1024) {
        alert('ファイルサイズは2MB以下にしてください');
        return;
      }
      
      // ファイル形式チェック
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        alert('画像ファイル（JPEG, PNG, GIF, SVG）のみアップロード可能です');
        return;
      }
      
      // ファイルを状態に保存
      setMobileLogoFile(file);
      console.log('Mobile logo file set:', file.name);
      console.log('File type:', file.type);
      console.log('File size:', file.size, 'bytes');
      
      // プレビュー用にファイルを読み込む
      const reader = new FileReader();
      reader.onloadend = () => {
        setMobilePreviewUrl(reader.result);
        console.log('Mobile preview URL set');
      };
      reader.readAsDataURL(file);
      
      // ボタンの状態を強制的に更新
      setTimeout(() => {
        const uploadButton = document.getElementById('mobile-logo-upload-button');
        if (uploadButton && selectedCompany) {
          uploadButton.disabled = false;
          uploadButton.classList.remove('bg-gray-300', 'cursor-not-allowed');
          uploadButton.classList.add('bg-blue-500', 'hover:bg-blue-600', 'text-white');
        }
      }, 100);
    } else {
      console.error('No file selected in handleMobileFileChange');
    }
  };

  const handleUpload = async () => {
    if (!selectedCompany || !logoFile) {
      setUploadStatus('企業とロゴファイルを選択してください');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      formData.append('companyId', selectedCompany);

      const response = await fetch('/api/admin/upload-logo', {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token')
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'ロゴのアップロードに失敗しました');
      }

      setUploadStatus('ロゴを正常にアップロードしました');
      setTimeout(() => {
        setUploadStatus('');
      }, 3000);
    } catch (err) {
      setUploadStatus(`エラー: ${err.message}`);
    }
  };

  const handleMobileUpload = async () => {
    console.log('handleMobileUpload called');
    console.log('selectedCompany:', selectedCompany);
    console.log('mobileLogoFile:', mobileLogoFile);
    
    // 企業が選択されていない場合はエラー
    if (!selectedCompany) {
      console.error('No company selected for mobile logo upload');
      setMobileUploadStatus('企業を選択してください');
      return;
    }

    try {
      console.log('Preparing FormData for mobile logo upload');
      const formData = new FormData();
      
      // ファイルが選択されている場合のみ追加
      if (mobileLogoFile) {
        formData.append('mobileLogo', mobileLogoFile);
        console.log('Added mobile logo file to FormData:', mobileLogoFile.name);
      } else {
        console.log('No file selected, this will remove the existing mobile logo');
      }
      
      formData.append('companyId', selectedCompany);
      
      // FormDataの内容をログに出力
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      
      console.log('Sending mobile logo upload request');
      const response = await fetch('/api/admin/upload-mobile-logo', {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token')
        },
        body: formData
      });
      
      console.log('Mobile logo upload response status:', response.status);
      console.log('Mobile logo upload response headers:', response.headers);

      // レスポンスの内容をテキストとして取得
      const responseText = await response.text();
      console.log('Response text:', responseText);

      if (!response.ok) {
        console.error('Mobile logo upload error - Response text:', responseText);
        
        // JSONとして解析できるか試みる
        let errorMessage = 'モバイルロゴのアップロードに失敗しました';
        try {
          const errorData = JSON.parse(responseText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (jsonError) {
          console.error('Error parsing JSON:', jsonError);
          // HTMLレスポンスの場合、ステータスコードを含めたエラーメッセージを表示
          errorMessage = `サーバーエラー (${response.status}): モバイルロゴのアップロードに失敗しました`;
        }
        
        throw new Error(errorMessage);
      }

      console.log('Mobile logo upload successful');
      setMobileUploadStatus('モバイルロゴを正常にアップロードしました');
      setTimeout(() => {
        setMobileUploadStatus('');
      }, 3000);
    } catch (err) {
      console.error('Error in handleMobileUpload:', err);
      setMobileUploadStatus(`エラー: ${err.message}`);
    }
  };

  // mobileLogoFileの状態変化を監視
  useEffect(() => {
    console.log('mobileLogoFile state changed:', mobileLogoFile);
    console.log('Upload button should be enabled:', !!(selectedCompany && mobileLogoFile));
  }, [mobileLogoFile, selectedCompany]);

  // タブが変更されたときに対応するデータを取得
  useEffect(() => {
    if (activeTab === 'clientCompanies') {
      fetchClientCompanies();
    } else if (activeTab === 'employmentTypes') {
      fetchEmploymentTypes();
    }
  }, [activeTab]);
  
  // クライアント企業が選択されたときに担当業務を取得
  useEffect(() => {
    if (selectedClientCompany) {
      fetchJobAssignments(selectedClientCompany);
    } else {
      setJobAssignments([]);
    }
  }, [selectedClientCompany]);

  const fetchClientCompanies = async () => {
    try {
      const response = await fetch('/api/admin/client-companies', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('クライアント企業の取得に失敗しました');
      }

      const data = await response.json();
      setClientCompanies(data);
    } catch (error) {
      console.error('Error fetching client companies:', error);
      setError('クライアント企業の取得に失敗しました');
    }
  };
  
  // 担当業務の取得
  const fetchJobAssignments = async (clientCompanyId) => {
    try {
      const response = await fetch(`/api/admin/client-companies/${clientCompanyId}/job-assignments`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('担当業務の取得に失敗しました');
      }

      const data = await response.json();
      setJobAssignments(data);
    } catch (error) {
      console.error('Error fetching job assignments:', error);
      setError('担当業務の取得に失敗しました');
    }
  };
  
  // 雇用区分の取得
  const fetchEmploymentTypes = async () => {
    try {
      const response = await fetch('/api/admin/employment-types', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('雇用区分の取得に失敗しました');
      }

      const data = await response.json();
      setEmploymentTypes(data);
    } catch (error) {
      console.error('Error fetching employment types:', error);
      setError('雇用区分の取得に失敗しました');
    }
  };

  // クライアント企業の追加/更新
  const handleSaveClientCompany = async () => {
    try {
      const url = isEditing 
        ? `/api/admin/client-companies/${currentItem.id}`
        : '/api/admin/client-companies';
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: currentItem.name,
          description: currentItem.description
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'クライアント企業の保存に失敗しました');
      }

      setShowClientCompanyModal(false);
      setCurrentItem({ name: '', description: '' });
      setIsEditing(false);
      fetchClientCompanies();
      setMessage(isEditing ? 'クライアント企業を更新しました' : 'クライアント企業を追加しました');
    } catch (error) {
      console.error('Error saving client company:', error);
      setError(error.message);
    }
  };
  
  // 担当業務の追加/更新
  const handleSaveJobAssignment = async () => {
    try {
      const url = isEditing 
        ? `/api/admin/job-assignments/${currentItem.id}`
        : `/api/admin/client-companies/${selectedClientCompany}/job-assignments`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: currentItem.name,
          description: currentItem.description
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '担当業務の保存に失敗しました');
      }

      setShowJobAssignmentModal(false);
      setCurrentItem({ name: '', description: '' });
      setIsEditing(false);
      fetchJobAssignments(selectedClientCompany);
      setMessage(isEditing ? '担当業務を更新しました' : '担当業務を追加しました');
    } catch (error) {
      console.error('Error saving job assignment:', error);
      setError(error.message);
    }
  };
  
  // 雇用区分の追加/更新
  const handleSaveEmploymentType = async () => {
    try {
      const url = isEditing 
        ? `/api/admin/employment-types/${currentItem.id}`
        : '/api/admin/employment-types';
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: currentItem.name,
          description: currentItem.description
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '雇用区分の保存に失敗しました');
      }

      setShowEmploymentTypeModal(false);
      setCurrentItem({ name: '', description: '' });
      setIsEditing(false);
      fetchEmploymentTypes();
      setMessage(isEditing ? '雇用区分を更新しました' : '雇用区分を追加しました');
    } catch (error) {
      console.error('Error saving employment type:', error);
      setError(error.message);
    }
  };
  
  // クライアント企業の削除
  const handleDeleteClientCompany = async (id) => {
    if (!window.confirm('このクライアント企業を削除しますか？関連する担当業務も削除されます。')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/admin/client-companies/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': localStorage.getItem('token')
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'クライアント企業の削除に失敗しました');
      }

      fetchClientCompanies();
      setMessage('クライアント企業を削除しました');
    } catch (error) {
      console.error('Error deleting client company:', error);
      setError(error.message);
    }
  };
  
  // 担当業務の削除
  const handleDeleteJobAssignment = async (id) => {
    if (!window.confirm('この担当業務を削除しますか？')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/admin/job-assignments/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': localStorage.getItem('token')
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '担当業務の削除に失敗しました');
      }

      fetchJobAssignments(selectedClientCompany);
      setMessage('担当業務を削除しました');
    } catch (error) {
      console.error('Error deleting job assignment:', error);
      setError(error.message);
    }
  };
  
  // 雇用区分の削除
  const handleDeleteEmploymentType = async (id) => {
    if (!window.confirm('この雇用区分を削除しますか？')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/admin/employment-types/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': localStorage.getItem('token')
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '雇用区分の削除に失敗しました');
      }

      fetchEmploymentTypes();
      setMessage('雇用区分を削除しました');
    } catch (error) {
      console.error('Error deleting employment type:', error);
      setError(error.message);
    }
  };
  
  // クライアント企業の編集モーダルを開く
  const handleEditClientCompany = (company) => {
    setCurrentItem(company);
    setIsEditing(true);
    setShowClientCompanyModal(true);
  };
  
  // 担当業務の編集モーダルを開く
  const handleEditJobAssignment = (jobAssignment) => {
    setCurrentItem(jobAssignment);
    setIsEditing(true);
    setShowJobAssignmentModal(true);
  };
  
  // 雇用区分の編集モーダルを開く
  const handleEditEmploymentType = (employmentType) => {
    setCurrentItem(employmentType);
    setIsEditing(true);
    setShowEmploymentTypeModal(true);
  };
  
  // 新規追加モーダルを開く
  const handleAddNew = (type) => {
    setCurrentItem({ name: '', description: '' });
    setIsEditing(false);
    
    if (type === 'clientCompany') {
      setShowClientCompanyModal(true);
    } else if (type === 'jobAssignment') {
      if (!selectedClientCompany) {
        setError('先にクライアント企業を選択してください');
        return;
      }
      setShowJobAssignmentModal(true);
    } else if (type === 'employmentType') {
      setShowEmploymentTypeModal(true);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">読み込み中...</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  // 企業IDのリストを取得（ユーザーから一意の企業IDを抽出）
  const uniqueCompanies = [...new Set(companies.map(company => company.company_id))].map(companyId => {
    const company = companies.find(c => c.company_id === companyId);
    return {
      id: companyId,
      name: company ? company.company_name || companyId : companyId
    };
  });

  return (
    <Container className="mt-4">
      <h2>スーパー管理者ダッシュボード</h2>
      
      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="companies" title="企業ロゴ">
          <Card className="mb-4">
            <Card.Header>企業ロゴ管理</Card.Header>
            <Card.Body>
              {/* 既存のロゴ管理UI */}
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>企業を選択</Form.Label>
                  <Form.Control 
                    as="select" 
                    value={selectedCompany} 
                    onChange={handleCompanyChange}
                  >
                    <option value="">選択してください</option>
                    {uniqueCompanies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name} ({company.id})
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>通常ロゴ</Form.Label>
                      <Form.Control 
                        type="file" 
                        onChange={handleFileChange}
                        accept="image/jpeg,image/png,image/gif,image/svg+xml"
                      />
                      <Button 
                        variant="primary" 
                        onClick={handleUpload} 
                        className="mt-2"
                        disabled={!logoFile || !selectedCompany}
                        id="upload-logo-button"
                      >
                        アップロード
                      </Button>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>モバイルロゴ</Form.Label>
                      <Form.Control 
                        type="file" 
                        onChange={handleMobileFileChange}
                        accept="image/jpeg,image/png,image/gif,image/svg+xml"
                      />
                      <Button 
                        variant="primary" 
                        onClick={handleMobileUpload} 
                        className="mt-2"
                        disabled={!selectedCompany}
                        id="upload-mobile-logo-button"
                      >
                        アップロード
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="clientCompanies" title="クライアント企業">
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>クライアント企業管理</span>
              <Button variant="primary" size="sm" onClick={() => handleAddNew('clientCompany')}>
                新規追加
              </Button>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>企業名</th>
                    <th>説明</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {clientCompanies.length > 0 ? (
                    clientCompanies.map(company => (
                      <tr key={company.id}>
                        <td>{company.id}</td>
                        <td>{company.name}</td>
                        <td>{company.description}</td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-2"
                            onClick={() => handleEditClientCompany(company)}
                          >
                            編集
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDeleteClientCompany(company.id)}
                          >
                            削除
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">クライアント企業がありません</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="jobAssignments" title="担当業務">
          <Card className="mb-4">
            <Card.Header>担当業務管理</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>クライアント企業を選択</Form.Label>
                <Form.Control 
                  as="select" 
                  value={selectedClientCompany} 
                  onChange={(e) => setSelectedClientCompany(e.target.value)}
                >
                  <option value="">選択してください</option>
                  {clientCompanies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              
              <div className="d-flex justify-content-end mb-3">
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={() => handleAddNew('jobAssignment')}
                  disabled={!selectedClientCompany}
                >
                  新規追加
                </Button>
              </div>
              
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>業務名</th>
                    <th>説明</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {jobAssignments.length > 0 ? (
                    jobAssignments.map(job => (
                      <tr key={job.id}>
                        <td>{job.id}</td>
                        <td>{job.name}</td>
                        <td>{job.description}</td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-2"
                            onClick={() => handleEditJobAssignment(job)}
                          >
                            編集
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDeleteJobAssignment(job.id)}
                          >
                            削除
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        {selectedClientCompany ? '担当業務がありません' : 'クライアント企業を選択してください'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="employmentTypes" title="雇用区分">
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>雇用区分管理</span>
              <Button variant="primary" size="sm" onClick={() => handleAddNew('employmentType')}>
                新規追加
              </Button>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>雇用区分名</th>
                    <th>説明</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {employmentTypes.length > 0 ? (
                    employmentTypes.map(type => (
                      <tr key={type.id}>
                        <td>{type.id}</td>
                        <td>{type.name}</td>
                        <td>{type.description}</td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-2"
                            onClick={() => handleEditEmploymentType(type)}
                          >
                            編集
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDeleteEmploymentType(type.id)}
                          >
                            削除
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">雇用区分がありません</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
      
      {/* クライアント企業モーダル */}
      <Modal show={showClientCompanyModal} onHide={() => setShowClientCompanyModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'クライアント企業を編集' : 'クライアント企業を追加'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>企業名</Form.Label>
              <Form.Control 
                type="text" 
                value={currentItem.name || ''} 
                onChange={(e) => setCurrentItem({...currentItem, name: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>説明</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                value={currentItem.description || ''} 
                onChange={(e) => setCurrentItem({...currentItem, description: e.target.value})}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClientCompanyModal(false)}>
            キャンセル
          </Button>
          <Button variant="primary" onClick={handleSaveClientCompany}>
            保存
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* 担当業務モーダル */}
      <Modal show={showJobAssignmentModal} onHide={() => setShowJobAssignmentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? '担当業務を編集' : '担当業務を追加'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>業務名</Form.Label>
              <Form.Control 
                type="text" 
                value={currentItem.name || ''} 
                onChange={(e) => setCurrentItem({...currentItem, name: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>説明</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                value={currentItem.description || ''} 
                onChange={(e) => setCurrentItem({...currentItem, description: e.target.value})}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowJobAssignmentModal(false)}>
            キャンセル
          </Button>
          <Button variant="primary" onClick={handleSaveJobAssignment}>
            保存
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* 雇用区分モーダル */}
      <Modal show={showEmploymentTypeModal} onHide={() => setShowEmploymentTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? '雇用区分を編集' : '雇用区分を追加'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>雇用区分名</Form.Label>
              <Form.Control 
                type="text" 
                value={currentItem.name || ''} 
                onChange={(e) => setCurrentItem({...currentItem, name: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>説明</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                value={currentItem.description || ''} 
                onChange={(e) => setCurrentItem({...currentItem, description: e.target.value})}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEmploymentTypeModal(false)}>
            キャンセル
          </Button>
          <Button variant="primary" onClick={handleSaveEmploymentType}>
            保存
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default SuperAdminDashboard; 